import React, { useEffect, useState } from 'react';
import { AlertCircle, FileInput } from 'lucide-react';
import { useToast } from '../../../../../hooks/useToast';
import { usePaymentMethods } from '../../../../../hooks/usePaymentMethods';
import { ChartAccount, listChartOfAccounts } from '../../../../../services/ChartOfAccountsService';
import { JournalLine, postJournalEntry } from '../../../../../services/JournalEntriesService';
import { listSuppliers } from '../../../../../services/SuppliersService';
import { Supplier } from '../../../../../types/Supplier.types';
import { formatEuro } from '../masters/financeMasters';
import { AttachmentsField, FormSkeleton, JournalPreview, SearchSelect, addDays, today } from './FormParts';
import '../../shared-modules.css';
import './FinanceForms.css';

const VAT_OPTIONS = [0, 4, 10, 21];
const IRPF_OPTIONS = [0, 15, 19];
const DEFAULT_DUE_DAYS = 30;

const round2 = (n: number) => Math.round(n * 100) / 100;

interface ReceivedInvoiceFormProps {
  privacyMode: boolean;
  onDone?: () => void;
}

export const ReceivedInvoiceForm = ({ privacyMode, onDone }: ReceivedInvoiceFormProps) => {
  const { notify } = useToast();

  const { methods } = usePaymentMethods();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);

  const [supplierId, setSupplierId] = useState('');
  const [docNumber, setDocNumber] = useState('');
  const [date, setDate] = useState(today());
  const [dueDate, setDueDate] = useState(addDays(today(), DEFAULT_DUE_DAYS));
  const [base, setBase] = useState(0);
  const [vatRate, setVatRate] = useState(21);
  const [irpfRate, setIrpfRate] = useState(0);
  const [expenseAccount, setExpenseAccount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Transferencia bancaria');
  const [attachments, setAttachments] = useState<File[]>([]);

  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [supplierList, chart] = await Promise.all([listSuppliers({ status: 'active' }), listChartOfAccounts()]);
        if (cancelled) return;
        setSuppliers(supplierList);
        setAccounts(chart);
        const purchases = chart.find(a => a.code.startsWith('600'));
        setExpenseAccount(purchases ? purchases.code : '');
        setLoadError('');
      } catch (err) {
        console.error('Error loading received invoice form data:', err);
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'No se pudieron cargar proveedores y cuentas.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const supplier = suppliers.find(s => s.id === supplierId);
  const dueDays = supplier && supplier.paymentTermsDays !== null ? supplier.paymentTermsDays : DEFAULT_DUE_DAYS;

  // El vencimiento se propone con los días de pago del proveedor
  const handleSupplierChange = (id: string) => {
    setSupplierId(id);
    const s = suppliers.find(x => x.id === id);
    const days = s && s.paymentTermsDays !== null ? s.paymentTermsDays : DEFAULT_DUE_DAYS;
    if (date) setDueDate(addDays(date, days));
  };

  const handleDateChange = (value: string) => {
    setDate(value);
    if (value) setDueDate(addDays(value, dueDays));
  };

  const expense = accounts.find(a => a.code === expenseAccount);
  const vatAmount = round2(base * vatRate / 100);
  const irpfAmount = round2(base * irpfRate / 100);
  const total = round2(base + vatAmount - irpfAmount);

  const journalLines: JournalLine[] = [
    { account: expenseAccount, accountName: expense ? expense.name : 'Cuenta de gasto', debit: base, credit: 0 },
    { account: '472000', accountName: 'H.P. IVA Soportado', debit: vatAmount, credit: 0 },
    { account: '400000', accountName: 'Proveedores (Euros)', debit: 0, credit: total },
    { account: '475100', accountName: 'H.P. Acreedora por retenciones practicadas', debit: 0, credit: irpfAmount }
  ];

  const validate = () => {
    if (!supplier) return 'Seleccione un proveedor.';
    if (!docNumber.trim()) return 'El número de la factura del proveedor es obligatorio.';
    if (!date || !dueDate) return 'La fecha y el vencimiento son obligatorios.';
    if (dueDate < date) return 'El vencimiento no puede ser anterior a la fecha de la factura.';
    if (base <= 0) return 'La base imponible debe ser mayor que cero.';
    if (!expenseAccount) return 'Seleccione la cuenta de gasto.';
    return '';
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      await postJournalEntry(`Factura recibida ${docNumber.trim()} de ${supplier.name}`, journalLines, 'Facturas Recibidas', accounts, date);
    } catch (err) {
      console.error('Error posting received invoice journal entry:', err);
      setFormError(`No se pudo contabilizar la factura: ${err instanceof Error ? err.message : 'error del servidor.'}`);
      setSaving(false);
      return;
    }
    const retention = irpfAmount > 0 ? ' + 4751' : '';
    notify('Factura contabilizada', `Factura ${docNumber.trim()} de ${supplier.name} contabilizada en el Libro Diario [${expenseAccount.slice(0, 3)} + 472 a 400${retention}].`, 'info');
    setSaving(false);
    if (onDone) onDone();
  };

  if (loading) return <FormSkeleton />;

  if (loadError) {
    return (
      <div className="module-panel">
        <div className="module-panel-empty">{loadError}</div>
      </div>
    );
  }

  const supplierOptions = suppliers.map(s => ({ value: s.id, label: s.name, sub: s.cif ? `CIF: ${s.cif}` : undefined }));
  const expenseOptions = accounts
    .filter(a => a.code.startsWith('6') && a.code.length >= 3)
    .map(a => ({ value: a.code, label: `${a.code} · ${a.name}` }));

  return (
    <div className="fin-doc">
      <div className="fin-doc__header">
        <div>
          <span className="fin-doc__tag">COMPRAS Y PAGOS • FACTURAS RECIBIDAS</span>
          <h3 className="fin-doc__title"><FileInput size={14} /> Factura recibida</h3>
        </div>
        <span className="fin-doc__badge">{irpfAmount > 0 ? 'CON RETENCIÓN IRPF' : 'SIN RETENCIÓN'}</span>
      </div>

      {formError && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{formError}</span>
        </div>
      )}

      <div className="fin-doc__grid">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Proveedor (buscador) *</label>
          <SearchSelect
            options={supplierOptions}
            value={supplierId}
            onChange={handleSupplierChange}
            placeholder="Buscar proveedor por nombre o CIF..."
            emptyText="No hay proveedores"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Nº factura de origen *</label>
          <input
            type="text"
            className="sidebar-form-input fin-doc__mono"
            value={docNumber}
            onChange={(e) => setDocNumber(e.target.value)}
            placeholder="Ej. FACT-2026/092"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Forma de pago *</label>
          <select className="sidebar-form-input" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
            {methods.filter(m => m.isActive || m.name === paymentMethod).map(m => (
              <option key={m.id} value={m.name}>{m.name}</option>
            ))}
          </select>
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Fecha *</label>
          <input
            type="date"
            required
            className="sidebar-form-input"
            value={date}
            onChange={(e) => handleDateChange(e.target.value)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Vencimiento *</label>
          <input
            type="date"
            required
            className="sidebar-form-input"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Cuenta de gasto PGC *</label>
          <SearchSelect
            options={expenseOptions}
            value={expenseAccount}
            onChange={setExpenseAccount}
            placeholder="Buscar cuenta 6xx..."
            emptyText="No hay cuentas de gasto"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Base imponible (€) *</label>
          <input
            type="number"
            min={0}
            step="0.01"
            className="sidebar-form-input"
            value={base}
            onChange={(e) => setBase(parseFloat(e.target.value) || 0)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">IVA *</label>
          <select className="sidebar-form-input" value={vatRate} onChange={(e) => setVatRate(parseInt(e.target.value, 10) || 0)}>
            {VAT_OPTIONS.map(r => <option key={r} value={r}>{r}%</option>)}
          </select>
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Retención IRPF *</label>
          <select className="sidebar-form-input" value={irpfRate} onChange={(e) => setIrpfRate(parseInt(e.target.value, 10) || 0)}>
            {IRPF_OPTIONS.map(r => <option key={r} value={r}>{r}%</option>)}
          </select>
        </div>
      </div>

      <div className="fin-doc__totals">
        <span>Base: <strong>{formatEuro(base, privacyMode)}</strong></span>
        <span>IVA soportado: <strong>{formatEuro(vatAmount, privacyMode)}</strong></span>
        <span>Retención IRPF: <strong>-{formatEuro(irpfAmount, privacyMode)}</strong></span>
        <span className="fin-doc__total">Total a pagar: <strong>{formatEuro(total, privacyMode)}</strong></span>
      </div>

      <AttachmentsField files={attachments} onChange={setAttachments} />

      <JournalPreview lines={journalLines} privacyMode={privacyMode} />

      <div className="fin-doc__actions">
        <button type="button" className="fin-doc__btn" disabled title="Pendiente de endpoint">
          Guardar borrador
        </button>
        <button type="button" className="fin-doc__btn fin-doc__btn--primary" disabled={saving} onClick={handleSubmit}>
          {saving ? 'Contabilizando...' : 'Contabilizar'}
        </button>
      </div>
    </div>
  );
};

export default ReceivedInvoiceForm;
